import Reveal from './Reveal';
import React, { useState } from 'react';
import './OVRPlus.css';

const BENEFITS = [
  '10% off all drop-ins',
  '15% off regular tournaments',
  '20% off Blitz tournaments',
  'Priority free agent placement',
  'Early access to registration',
  'Early access to clothing',
  'Member-only updates and perks',
];

const PLANS = [
  {
    id: 'starter',
    duration: '3 Month Pass',
    subtitle: 'STARTER',
    price: '$49.99',
    description: 'Perfect for players who want to try OVR+ and save on upcoming runs.',
    perks: [
      'Full OVR+ benefits',
      '10% off drop-ins',
      '15% off tournaments',
      '20% off Blitz tournaments',
      'Priority free agent placement',
    ],
    highlight: false,
    tag: null,
  },
  {
    id: 'popular',
    duration: '6 Month Pass',
    subtitle: 'MOST POPULAR',
    price: '$89.99',
    description: 'Best for regular players who know they\'ll be showing up throughout the season.',
    perks: [
      'Full OVR+ benefits',
      'Better value than the 3 month pass',
      'Early access to registration',
      'Priority waitlist placement',
    ],
    highlight: true,
    tag: 'MOST POPULAR',
  },
  {
    id: 'value',
    duration: '12 Month Pass',
    subtitle: 'BEST VALUE',
    price: '$149.99',
    description: 'For the players who are all-in on OVR for the year.',
    perks: [
      'Full OVR+ benefits',
      'Best overall value',
      'Member-only perks',
      'Priority access to major events',
    ],
    highlight: false,
    tag: 'BEST VALUE',
  },
];

function WaitlistModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitted(true);
    setError('');
  };

  return (
    <div className="ovrplus-modal-overlay" onClick={onClose}>
      <div className="ovrplus-modal" onClick={e => e.stopPropagation()}>
        <button className="ovrplus-modal__close" onClick={onClose} aria-label="Close">✕</button>

        {!submitted ? (
          <>
            <div className="ovrplus-modal__eyebrow">OVR+ WAITLIST</div>
            <h2 className="ovrplus-modal__title">BE FIRST IN LINE</h2>
            <p className="ovrplus-modal__body">
              OVR+ is launching soon. Drop your email and we'll hit you the moment memberships go live - before anyone else.
            </p>
            <form onSubmit={handleSubmit} className="ovrplus-modal__form">
              <input
                type="email"
                className="ovrplus-modal__input"
                placeholder="your@email.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                autoFocus
              />
              {error && <p className="ovrplus-modal__error">{error}</p>}
              <button type="submit" className="ovrplus-modal__submit">
                NOTIFY ME WHEN IT DROPS →
              </button>
            </form>
            <p className="ovrplus-modal__hint">No spam. Just the drop.</p>
          </>
        ) : (
          <>
            <div className="ovrplus-modal__eyebrow">YOU'RE IN</div>
            <h2 className="ovrplus-modal__title">WE'LL HIT YOU FIRST</h2>
            <p className="ovrplus-modal__body">
              You're on the OVR+ waitlist. When memberships go live, you'll be first to know. Stay locked in.
            </p>
            <button className="ovrplus-modal__submit" onClick={onClose}>
              BACK TO OVR+ →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function OVRPlus({ onNavigate }) {
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <div className="ovrplus-page">

      {/* Back */}
      <button
        onClick={() => onNavigate('HOME')}
        style={{ position: 'absolute', top: 24, left: 24, zIndex: 10, background: 'transparent', border: 'none', color: '#666', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}
      >
        ← BACK
      </button>

      {/* Hero */}
      <section className="ovrplus-hero ovr-stagger">
        <div className="ovrplus-hero__eyebrow">COMING SOON</div>
        <h1 className="ovrplus-hero__title">OVR<span className="ovrplus-hero__plus">+</span></h1>
        <p className="ovrplus-hero__sub">
          OVR+ is built for the players who show up often, compete hard, and want more from the community.
        </p>
        <div className="ovrplus-hero__badge">ONE MEMBERSHIP · ONE SET OF PERKS · THREE DURATIONS</div>
      </section>

      {/* Benefits strip */}
      <Reveal as="section" className="ovrplus-benefits">
        <div className="ovrplus-benefits__label">EVERY PLAN INCLUDES</div>
        <div className="ovrplus-benefits__grid">
          {BENEFITS.map((b, i) => (
            <div key={i} className="ovrplus-benefits__item">
              <span className="ovrplus-benefits__check">✓</span>
              <span>{b}</span>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Divider */}
      <div className="ovrplus-divider" />

      {/* Pricing */}
      <section className="ovrplus-pricing">
        <div className="ovrplus-pricing__eyebrow">CHOOSE YOUR PASS</div>
        <h2 className="ovrplus-pricing__heading">LOCK IN YOUR SEASON.</h2>

        <Reveal variant="scale" delay={100} className="ovrplus-cards">
          {PLANS.map(plan => (
            <div
              key={plan.id}
              className={`ovrplus-card${plan.highlight ? ' ovrplus-card--popular' : ''}${plan.tag === 'BEST VALUE' ? ' ovrplus-card--value' : ''}`}
            >
              {plan.tag && (
                <div className={`ovrplus-card__tag${plan.highlight ? ' ovrplus-card__tag--popular' : ' ovrplus-card__tag--value'}`}>
                  {plan.tag}
                </div>
              )}

              <div className="ovrplus-card__subtitle">{plan.subtitle}</div>
              <h3 className="ovrplus-card__duration">{plan.duration}</h3>
              <p className="ovrplus-card__desc">{plan.description}</p>

              <div className="ovrplus-card__price-row">
                <span className="ovrplus-card__price">{plan.price}</span>
                <span className="ovrplus-card__price-note">· one-time</span>
              </div>

              <ul className="ovrplus-card__perks">
                {plan.perks.map((perk, i) => (
                  <li key={i} className="ovrplus-card__perk">
                    <span className="ovrplus-card__perk-check">✓</span>
                    {perk}
                  </li>
                ))}
              </ul>

              <div className="ovrplus-card__coming-wrap">
                <span className="ovrplus-card__coming">COMING SOON</span>
              </div>

              <button
                className={`ovrplus-card__cta${plan.highlight ? ' ovrplus-card__cta--popular' : ''}`}
                onClick={() => setShowWaitlist(true)}
              >
                JOIN THE WAITLIST →
              </button>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Bottom CTA */}
      <section className="ovrplus-cta-band">
        <div className="ovrplus-cta-band__inner">
          <h2 className="ovrplus-cta-band__title">GET EARLY ACCESS</h2>
          <p className="ovrplus-cta-band__sub">
            OVR+ is launching soon. Join the waitlist now and be the first to lock in your membership.
          </p>
          <button className="ovrplus-cta-band__btn" onClick={() => setShowWaitlist(true)}>
            JOIN THE OVR+ WAITLIST →
          </button>
        </div>
      </section>

      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
    </div>
  );
}
